import { useCallback, useEffect, useMemo, useState } from 'react';
import { getClassMembers } from '../services/classMembersService';
import {
  getAcademicCalendarSections,
  getClassScheduleSettings,
  getProgramColorSettings,
  loadAcademicCalendarSectionsCache,
  type ClassScheduleSettings,
  type PageSectionLayout,
  type ProgramColorSettings,
} from '../services/sectionLayoutService';
import type { ClassMemberRow } from '../utils/classMembersStorage';
import { ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT } from '../utils/calendarSectionAcademicYear';
import {
  ANNUAL_PREREQUISITE_REFRESH_EVENT,
  type AnnualPrerequisiteRefreshDetail,
} from '../utils/annualPrerequisiteRefresh';
import { CLASS_MEMBERS_CHANGED_EVENT } from '../utils/classMembersStorage';
import {
  computePresetMenuGradeStatus,
  type PresetMenuGradeStatus,
} from '../utils/presetMenuGradeStatus';
import type { Track } from '../types/domain';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const EMPTY_STATUS: PresetMenuGradeStatus = {
  academicCalendar: false,
  classSchedule: false,
  gradeTrack: false,
  classMembers: false,
  timetableSettings: false,
};

const DEFAULT_CLASS_SCHEDULE_SETTINGS: ClassScheduleSettings = {
  sections: [{ id: 'base', gradeIds: [] }],
  sectionRows: { base: [] },
  sectionWeekdayMax: { base: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0 } },
};

function readClassScheduleSettingsCache(workspaceId: string): ClassScheduleSettings | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(`cp.classScheduleSettings.v2.${workspaceId}`);
    if (!raw) return null;
    return JSON.parse(raw) as ClassScheduleSettings;
  } catch {
    return null;
  }
}

function readProgramColorSettingsCache(workspaceId: string): ProgramColorSettings | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = deviceCacheStorage.getItem(`cp.programColorSettings.v2.${workspaceId}`);
    if (!raw) return null;
    return JSON.parse(raw) as ProgramColorSettings;
  } catch {
    return null;
  }
}

function isPresetStatusStorageKey(key: string): boolean {
  return (
    key.includes('cp.academicCalendarSections.') ||
    key.includes('cp.classScheduleSettings.') ||
    key.includes('cp.gradeTrackSections.') ||
    key.includes('cp.programColorSettings.') ||
    key.includes('cp.academicSectionSettings.') ||
    key.includes('cp.academicSectionCalendarDays.') ||
    key.startsWith('cp-class-members:')
  );
}

export function usePresetMenuGradeStatus(params: {
  workspaceId: string | null;
  gradeId: string | null;
  gradeTrackSections: PageSectionLayout[];
  tracks: Track[];
  refreshEpoch?: number;
}): PresetMenuGradeStatus {
  const { workspaceId, gradeId, gradeTrackSections, tracks, refreshEpoch = 0 } = params;
  const [academicCalendarSections, setAcademicCalendarSections] = useState<PageSectionLayout[]>(() =>
    workspaceId ? loadAcademicCalendarSectionsCache(workspaceId) ?? [{ id: 'base', gradeIds: [] }] : [{ id: 'base', gradeIds: [] }]
  );
  const [classScheduleSettings, setClassScheduleSettings] = useState<ClassScheduleSettings>(() =>
    workspaceId ? readClassScheduleSettingsCache(workspaceId) ?? DEFAULT_CLASS_SCHEDULE_SETTINGS : DEFAULT_CLASS_SCHEDULE_SETTINGS
  );
  const [programColorSettings, setProgramColorSettings] = useState<ProgramColorSettings | null>(() =>
    workspaceId ? readProgramColorSettingsCache(workspaceId) : null
  );
  const [classMembers, setClassMembers] = useState<ClassMemberRow[]>([]);
  const [storageRev, setStorageRev] = useState(0);

  const reloadPresetSources = useCallback(async () => {
    if (!workspaceId) {
      setAcademicCalendarSections([{ id: 'base', gradeIds: [] }]);
      setClassScheduleSettings(DEFAULT_CLASS_SCHEDULE_SETTINGS);
      setProgramColorSettings(null);
      setClassMembers([]);
      return;
    }

    const cachedCalendar = loadAcademicCalendarSectionsCache(workspaceId);
    if (cachedCalendar) setAcademicCalendarSections(cachedCalendar);
    const cachedSchedule = readClassScheduleSettingsCache(workspaceId);
    if (cachedSchedule) setClassScheduleSettings(cachedSchedule);
    const cachedColors = readProgramColorSettingsCache(workspaceId);
    if (cachedColors) setProgramColorSettings(cachedColors);

    const gid = (gradeId ?? '').trim();
    try {
      const requests: [
        Promise<PageSectionLayout[]>,
        Promise<ClassScheduleSettings>,
        Promise<ProgramColorSettings>,
        Promise<ClassMemberRow[]> | null,
      ] = [
        getAcademicCalendarSections(workspaceId).catch(() => [{ id: 'base', gradeIds: [] }] as PageSectionLayout[]),
        getClassScheduleSettings(workspaceId).catch(() => cachedSchedule ?? DEFAULT_CLASS_SCHEDULE_SETTINGS),
        getProgramColorSettings(workspaceId).catch(() => cachedColors ?? { colorsByGrade: {}, baseTimetableByGrade: {}, workspaceFallback: null }),
        gid ? getClassMembers(workspaceId, gid).catch(() => [] as ClassMemberRow[]) : null,
      ];
      const [calendarSections, scheduleSettings, colorSettings, members] = await Promise.all([
        requests[0],
        requests[1],
        requests[2],
        requests[3] ?? Promise.resolve([] as ClassMemberRow[]),
      ]);
      setAcademicCalendarSections(calendarSections);
      setClassScheduleSettings(scheduleSettings);
      setProgramColorSettings(colorSettings);
      setClassMembers(members);
    } catch {
      /* cache-first: ignore network errors */
    }
  }, [workspaceId, gradeId]);

  useEffect(() => {
    void reloadPresetSources();
  }, [reloadPresetSources, refreshEpoch, storageRev]);

  useEffect(() => {
    if (!workspaceId || typeof window === 'undefined') return;

    const bump = () => setStorageRev((value) => value + 1);

    const onStorage = (event: StorageEvent) => {
      const key = event.key ?? '';
      if (isPresetStatusStorageKey(key)) bump();
    };
    const onCalendarChange = () => bump();
    const onPrerequisiteRefresh = (event: Event) => {
      const detail = (event as CustomEvent<AnnualPrerequisiteRefreshDetail>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      bump();
    };
    const onClassMembersChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId: string; gradeId: string }>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      const gid = (gradeId ?? '').trim();
      if (gid && detail.gradeId !== gid) return;
      bump();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, onCalendarChange);
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onPrerequisiteRefresh);
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onClassMembersChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, onCalendarChange);
      window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onPrerequisiteRefresh);
      window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onClassMembersChanged);
    };
  }, [workspaceId, gradeId]);

  return useMemo(() => {
    const gid = (gradeId ?? '').trim();
    if (!workspaceId || !gid) return EMPTY_STATUS;
    return computePresetMenuGradeStatus({
      workspaceId,
      gradeId: gid,
      academicCalendarSections,
      classScheduleSettings,
      gradeTrackSections,
      tracks,
      classMembers,
      programColorSettings,
    });
  }, [
    workspaceId,
    gradeId,
    academicCalendarSections,
    classScheduleSettings,
    gradeTrackSections,
    tracks,
    classMembers,
    programColorSettings,
  ]);
}
